// ---------------------------------------------------------------------------
// Shared types, copy, and helpers for the contact (email/phone) sections.
// ---------------------------------------------------------------------------

export type ContactType = 'email' | 'phone';

export interface ContactRowData {
  id: number;
  value: string;
  isPrimary: boolean;
  verified: boolean;
}

export interface EditingState {
  id: number | null;
  value: string;
  error: string | null;
}

export interface AddState {
  open: boolean;
  value: string;
  saving: boolean;
  error: string | null;
}

export interface DeleteTarget {
  type: ContactType;
  id: number;
  value: string;
}

export interface PendingProposal {
  otp_id: string;
  contact_type: ContactType;
  contact_value: string;
  replace_id: number | null;
}

/**
 * Whether a contact row may be removed: there must be more than one contact
 * overall, and a primary can only be removed if a same-type verified peer or
 * any cross-type contact remains.
 */
export function canRemoveContact(
  row: ContactRowData,
  sameTypeRows: ContactRowData[],
  crossTypeCount: number
): boolean {
  if (sameTypeRows.length + crossTypeCount <= 1) {
    return false;
  }
  if (!row.isPrimary) {
    return true;
  }
  return (
    sameTypeRows.some((r) => r.id !== row.id && r.verified) ||
    crossTypeCount > 0
  );
}

export const contactCopy = {
  email: {
    title: 'Email Addresses',
    primaryLabel: 'Primary and Username',
    otherLabel: 'Other Email',
    emptyText: 'No email addresses yet.',
    primaryHint:
      'Your primary email is your username. Changing it will update your login credentials.',
    addTitle: 'Add Email Address',
    addLabel: 'Email address',
    addButton: 'Email',
  },
  phone: {
    title: 'Phone Numbers',
    primaryLabel: 'Primary Phone',
    otherLabel: 'Phone',
    emptyText: 'No phone numbers yet.',
    primaryHint:
      'Changing your primary phone will also update your login contact.',
    addTitle: 'Add Phone Number',
    addLabel: 'Phone number',
    addButton: 'Phone',
  },
} as const;

/**
 * Primary-row hint. Pass *ownerName* when editing another person's contacts
 * (e.g. a household member) so the copy refers to them, not the actor.
 */
export function primaryHint(type: ContactType, ownerName?: string): string {
  const name = ownerName?.trim();
  if (!name) {
    return contactCopy[type].primaryHint;
  }
  return type === 'email'
    ? `${name}'s primary email is the username they sign in with. Changing it will update their login credentials.`
    : `Changing ${name}'s primary phone will also update their login contact.`;
}

/**
 * Warning shown above the email editor when acting on another person's
 * contacts: their primary email is also their login username.
 */
export function proxyLoginWarning(ownerName: string): string {
  return `Changing ${ownerName}'s primary email changes the email they use to sign in to Pronto.`;
}

export function addHelperText(type: ContactType, ownerName?: string): string {
  const viaSms = type === 'phone' ? ' via SMS' : '';
  const name = ownerName?.trim();
  if (name) {
    return `We'll send a verification code${viaSms} to confirm ownership. Once verified, this ${type} will become ${name}'s primary ${type}. ${name}'s previous primary will be kept on the account as a secondary contact and can be switched back at any time.`;
  }
  return `We'll send a verification code${viaSms} to confirm ownership. Once verified, this ${type} will become your primary ${type}. Your previous primary will be kept on your account as a secondary contact and you can switch back at any time.`;
}

export function contactError(type: ContactType, verb: string): string {
  return `Could not ${verb} ${type}. Please try again.`;
}
